import type { Academy, AcademyAnnouncementBanner } from '~/generated_types';
import { useTheme } from 'styled-components';
import { Col, Icon, Row } from '@workramp/ui';
import { useEffect, useRef } from 'react';
import { BaseThemeButton, BaseThemeRichText } from '~/clc/features/theming/learner/components';
import { useGetAcademyAnnouncementBannersForExternalUser } from '../../api';
import { useIsWithinTimeRange } from '../../utils';
import { AnnouncementBannerSectionEditorSettings } from '../AnnouncementBannerSettingsTabContent';
import { StyledBanner, StyledPreviewBadge } from './AcademyLearnerAnnouncementBanner.styles';

export const AcademyLearnerAnnouncementBanner = ({
  academyId,
  inAdminPreview = false,
  adminThemingSectionId,
  academyAnnouncementBanners = [],
  onBannerMounted,
}: {
  academyId: Academy['MINIMUM_THEME_CONTENT']['id'];
  inAdminPreview?: boolean;
  adminThemingSectionId?: string;
  academyAnnouncementBanners?: AcademyAnnouncementBanner['DEFAULT'][];
  onBannerMounted?: (ref: React.RefObject<HTMLDivElement>) => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const { data = [], isLoading } = useGetAcademyAnnouncementBannersForExternalUser(
    {
      academyId,
    },
    {
      enabled: true,
      initialData: inAdminPreview ? academyAnnouncementBanners : undefined,
    },
  );

  const defaultAnnouncementBar = data[0];
  const isPublished = useIsWithinTimeRange({
    startTime: defaultAnnouncementBar?.startTime,
    endTime: defaultAnnouncementBar?.endTime,
  });

  const shouldShowBanner =
    !isLoading &&
    defaultAnnouncementBar &&
    (isPublished || (inAdminPreview && adminThemingSectionId === AnnouncementBannerSectionEditorSettings.key));

  useEffect(() => {
    if (shouldShowBanner) {
      onBannerMounted?.(ref);
    }
  }, [shouldShowBanner, onBannerMounted]);

  if (!shouldShowBanner) {
    return null;
  }

  const { themeSettings } = defaultAnnouncementBar;

  return (
    <StyledBanner
      $backgroundColor={themeSettings.backgroundColor}
      ref={ref}
      role="region"
      aria-label="Announcement Banner"
    >
      {!isPublished && (
        <StyledPreviewBadge $textColor={themeSettings.textColor} $backgroundColor={themeSettings.backgroundColor}>
          <Icon name="eye-open:regular" size="sm" />
          Previewing
        </StyledPreviewBadge>
      )}
      <Row align="center" justify="center" gapX={4}>
        <Col>
          <BaseThemeRichText
            dangerouslySetInnerHTML={{ __html: themeSettings.message }}
            themeFontColor={themeSettings.textColor}
            themeFontBodySize={theme.typography.generateFontSize(2)}
          />
        </Col>
        {themeSettings.linkType !== 'none' && (
          <Col xs="auto">
            <BaseThemeButton
              href={themeSettings.linkUrl}
              target={themeSettings.linkOpenInNewTab ? '_blank' : '_self'}
              rel={themeSettings.linkOpenInNewTab ? 'noopener noreferrer' : undefined}
              themeTextColor={themeSettings.linkTextColor}
              themeColor={
                themeSettings.linkType === 'button' ? themeSettings.linkBackgroundColor : themeSettings.linkTextColor
              }
              themeBorderColor={themeSettings.linkBorderColor}
              variant={themeSettings.linkType === 'button' ? 'solid' : 'text'}
            >
              {themeSettings.linkType === 'button' ? themeSettings.linkText : <u>{themeSettings.linkText}</u>}
            </BaseThemeButton>
          </Col>
        )}
      </Row>
    </StyledBanner>
  );
};
